import clsx from 'clsx';
import { User, Users } from 'lucide-react';
import type { UserRef } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * What a task says of whoever has it: one person, or — given to a whole
 * department (operator, 2026-10-10: "i want to assign to department so everyone
 * in this department can work on it") — everyone in that department, who share
 * it. The API sends `assignedToDepartment` and the department with every task.
 */
export interface HeldTask {
  assignee?: UserRef | null;
  assignedToDepartment?: boolean;
  department?: { id: string; name: string; nameAr?: string | null } | null;
}

/** Whether a task is its whole department's, rather than one person's or nobody's. */
export function isDepartmentTask(task: HeldTask | null | undefined): boolean {
  return !!task?.assignedToDepartment && !!task.department;
}

/** Whoever has a task, in words — "Everyone in IT Development" for a department's — or null when nobody does. */
export function useTaskHolderName(): (task: HeldTask | null | undefined) => string | null {
  const { t, localized } = useI18n();
  return (task) => {
    if (task?.assignee) return localized(task.assignee);
    if (isDepartmentTask(task)) return t('tasks.everyoneIn', { department: localized(task!.department) });
    return null;
  };
}

/**
 * Whoever has a task, where a list or a card names them: the person, or the
 * department everyone in which shares it — with an icon when `icon` is set —
 * or "Unassigned", faint.
 */
export function TaskHolder({ task, icon = false, className }: { task: HeldTask; icon?: boolean; className?: string }) {
  const { t } = useI18n();
  const name = useTaskHolderName()(task);
  if (!name) return <span className={clsx('text-faint', className)}>{t('common.unassigned')}</span>;
  const Icon = isDepartmentTask(task) ? Users : User;
  return (
    <span className={clsx(icon && 'inline-flex min-w-0 items-center gap-1', className)}>
      {icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
      <span className={clsx(icon && 'truncate')}>{name}</span>
    </span>
  );
}

/** Whoever has a task, as plain words in the surrounding text — `empty` when nobody does. */
export function TaskHolderName({ task, empty }: { task: HeldTask; empty: string }) {
  return <>{useTaskHolderName()(task) ?? empty}</>;
}
